export type MonotoneSplinePoint = { x: number; y: number };

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * 单调三次样条：线条平滑，且保证任意两个相邻数据点之间的曲线不会冲出这两点的数值区间
 * （不会比两点里较小的那个还低，也不会比较大的那个还高）。普通 Catmull-Rom 在数据贴着
 * 0 附近、又夹着陡峭升降（比如"多天都是 0，突然一天冲高，又回落到 0"）时会甩出负值，
 * 看着像跌破了 0；裁剪掉那段溢出又会把曲线切成一段段不连续的弧，两边都不理想。
 *
 * 这个实现移植自留存分析模块（RetentionPage.tsx 的 buildSmoothSegment），那边已经用
 * 这套算法验证过效果，总览的交易趋势图直接复用，不用重新踩一遍同样的坑。
 */
export function buildMonotoneSplinePath(points: MonotoneSplinePoint[]) {
    if (points.length === 0) return '';
    if (points.length === 1) return `M ${round2(points[0].x)} ${round2(points[0].y)}`;

    const n = points.length;
    const dx: number[] = [];
    const slopes: number[] = [];

    for (let i = 0; i < n - 1; i += 1) {
        dx[i] = points[i + 1].x - points[i].x;
        slopes[i] = (points[i + 1].y - points[i].y) / dx[i];
    }

    // 端点切线取相邻斜率；中间点在拐向相反时压成 0，这样曲线不会越过极值点
    const tangents: number[] = [slopes[0]];

    for (let i = 1; i < n - 1; i += 1) {
        if (slopes[i - 1] * slopes[i] <= 0) {
            tangents[i] = 0;
        } else {
            const w1 = 2 * dx[i] + dx[i - 1];
            const w2 = dx[i] + 2 * dx[i - 1];
            tangents[i] = (w1 + w2) / (w1 / slopes[i - 1] + w2 / slopes[i]);
        }
    }

    tangents[n - 1] = slopes[n - 2];

    let path = `M ${round2(points[0].x)} ${round2(points[0].y)}`;

    for (let i = 0; i < n - 1; i += 1) {
        const c1x = points[i].x + dx[i] / 3;
        const c1y = points[i].y + (tangents[i] * dx[i]) / 3;
        const c2x = points[i + 1].x - dx[i] / 3;
        const c2y = points[i + 1].y - (tangents[i + 1] * dx[i]) / 3;

        path += ` C ${round2(c1x)} ${round2(c1y)}, ${round2(c2x)} ${round2(c2y)}, ${round2(
            points[i + 1].x
        )} ${round2(points[i + 1].y)}`;
    }

    return path;
}
